import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { attendanceService } from '@/services/api'
import {
  Download, Filter, Calendar, Users, Activity,
  Clock, ShieldCheck, MapPin, ChevronLeft, ChevronRight,
  ExternalLink, Search
} from 'lucide-react'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import Modal from '@/components/common/Modal'

// --- Конфигурация статусов в стиле SENTINEL ---
const STATUS_CONFIG = {
  on_time: { label: 'CLEARED', bg: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  late: { label: 'DELAYED', bg: 'bg-amber-50 text-amber-600 border-amber-100' },
  very_late: { label: 'CRITICAL', bg: 'bg-rose-50 text-rose-600 border-rose-100' },
  absent: { label: 'MISSING', bg: 'bg-slate-100 text-slate-500 border-slate-200' },
  no_schedule: { label: 'NO_PLAN', bg: 'bg-slate-50 text-slate-400 border-slate-200' },
  early: { label: 'EARLY', bg: 'bg-blue-50 text-blue-600 border-blue-100' },
}

export default function AttendancePage() {
  const today = format(new Date(), 'yyyy-MM-dd')
  const [filters, setFilters] = useState({
    date_from: today,
    date_to: today,
    person_type: '',
    event_type: '',
    attendance_status: '',
  })
  const [page, setPage] = useState(1)
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1) }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const params = { ...filters, ...(search ? { search } : {}), page }

  const { data, isLoading } = useQuery({
    queryKey: ['attendance-logs', params],
    queryFn: () => attendanceService.logs(params).then((r) => r.data),
    refetchInterval: 5000,
  })

  // Live WebSocket (Business Logic Preserved)
  const [liveEvents, setLiveEvents] = useState([])
  useEffect(() => {
    if (page !== 1 || filters.date_from !== today || search) {
      setLiveEvents([])
      return
    }
    const host = window.location.host
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    try {
      const ws = new WebSocket(`${protocol}://${host}/ws/attendance/`)
      ws.onmessage = (e) => {
        const event = JSON.parse(e.data)
        setLiveEvents((prev) => [event, ...prev].slice(0, 50))
      }
      return () => ws.close()
    } catch (err) { console.warn('WS Failed') }
  }, [page, filters.date_from, today, search])

  const logs = useMemo(() => {
    const fetched = data?.results || []
    if (page !== 1 || filters.date_from !== today || search) return fetched
    const fetchedIds = new Set(fetched.map(l => l.id))
    const uniqueLive = liveEvents.filter(l => !fetchedIds.has(l.id))
    return [...uniqueLive, ...fetched]
  }, [data, liveEvents, page, filters.date_from, today])

  const handleExport = async () => {
    const tid = toast.loading('Подготовка отчета...')
    try {
      const exportParams = { ...filters, ...(search ? { search } : {}) }
      const resp = await attendanceService.exportExcel(exportParams)
      const url = URL.createObjectURL(new Blob([resp.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = `attendance_${filters.date_from}.xlsx`
      a.click()
      toast.success('Отчет готов', { id: tid })
    } catch {
      toast.error('Ошибка экспорта', { id: tid })
    }
  }

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in duration-500">

      {/* HEADER */}
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white p-5 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-200">
              <ShieldCheck className="text-white w-5 h-5" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Protocol Logs</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black text-slate-900 tracking-tighter leading-none">Журнал посещаемости</h1>
          <p className="text-slate-500 text-sm font-medium mt-2">Анализ проходов и соблюдения графика в реальном времени</p>
        </div>

        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-200 hover:bg-slate-800 transition-all self-start md:self-auto"
        >
          <Download size={16} /> Экспорт Excel
        </button>
      </header>

      {/* FILTERS PANEL */}
      <section className="bg-white border border-slate-200 rounded-[2rem] p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6 px-2">
          <Filter size={16} className="text-indigo-600" />
          <span className="text-xs font-black uppercase tracking-widest text-slate-900">Фильтры системы</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="space-y-1.5 lg:col-span-2">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Поиск по имени</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Введите имя..."
                className="w-full bg-slate-50 border border-slate-100 rounded-xl pl-9 pr-4 py-3 text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Дата начала</label>
            <input
              type="date"
              className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Дата окончания</label>
            <input
              type="date"
              className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Категория</label>
            <select
              className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all appearance-none"
              value={filters.person_type}
              onChange={(e) => setFilters((f) => ({ ...f, person_type: e.target.value }))}
            >
              <option value="">Все типы</option>
              <option value="student">Студенты</option>
              <option value="employee">Сотрудники</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Направление</label>
            <select
              className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all appearance-none"
              value={filters.event_type}
              onChange={(e) => setFilters((f) => ({ ...f, event_type: e.target.value }))}
            >
              <option value="">Все события</option>
              <option value="in">Только Вход</option>
              <option value="out">Только Выход</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1">Протокол (Статус)</label>
            <select
              className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-all appearance-none"
              value={filters.attendance_status}
              onChange={(e) => setFilters((f) => ({ ...f, attendance_status: e.target.value }))}
            >
              <option value="">Любой статус</option>
              <option value="on_time">CLEARED</option>
              <option value="late">DELAYED</option>
              <option value="very_late">CRITICAL</option>
            </select>
          </div>
        </div>
      </section>

      {/* STATS MINI BAR */}
      {!isLoading && data && (
        <div className="flex items-center gap-6 px-4">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Найдено записей:</span>
            <span className="text-sm font-black text-slate-900">{data.count}</span>
          </div>
        </div>
      )}

      {/* TABLE SECTION */}
      <section className="bg-white border border-slate-200 rounded-[2rem] shadow-sm overflow-hidden">
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-slate-100">
          {isLoading ? (
            Array(5).fill(0).map((_, i) => (
              <div key={i} className="p-4 animate-pulse flex gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-slate-100 rounded w-3/4" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
            ))
          ) : logs.length === 0 ? (
            <div className="py-16 text-center text-slate-400 font-bold italic">Нет записей за выбранный период</div>
          ) : (
            logs.map((log) => {
              const status = STATUS_CONFIG[log.attendance_status] || STATUS_CONFIG.no_schedule
              return (
                <div key={log.id} className="p-4 flex items-center gap-3">
                  {log.photo ? (
                    <img src={log.photo} className="w-10 h-10 rounded-xl object-cover border border-slate-200 shrink-0 cursor-pointer" onClick={() => setSelectedPhoto(log.photo)} />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-500 border border-slate-200 uppercase shrink-0">
                      {log.person_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-slate-900 text-sm truncate">{log.person_name}</p>
                    <p className="text-[11px] text-slate-400 font-bold">
                      {format(new Date(log.timestamp), 'HH:mm:ss')} · {log.device_name || 'Terminal'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={clsx("px-2 py-0.5 rounded-lg text-[9px] font-black tracking-widest border", status.bg)}>{status.label}</span>
                    <span className={clsx("text-[10px] font-black uppercase", log.event_type === 'in' ? "text-emerald-500" : "text-slate-400")}>
                      {log.event_display}
                      {log.late_minutes > 0 && <span className="text-rose-500 ml-1">+{log.late_minutes}m</span>}
                    </span>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400 border-b border-slate-100">
                <th className="px-8 py-5">Время прохода</th>
                <th className="px-8 py-5">Идентификатор</th>
                <th className="px-8 py-5 text-center">Категория</th>
                <th className="px-8 py-5 text-center">Событие</th>
                <th className="px-8 py-5 text-center">Протокол</th>
                <th className="px-8 py-5 text-center">Отклонение</th>
                <th className="px-8 py-5">Устройство</th>
                <th className="px-8 py-5 text-right">Фото</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="py-20 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Чтение базы данных...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-20 text-center text-slate-400 font-bold italic">Нет записей за выбранный период</td>
                </tr>
              ) : (
                logs.map((log) => {
                  const status = STATUS_CONFIG[log.attendance_status] || STATUS_CONFIG.no_schedule
                  return (
                    <tr key={log.id} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-8 py-5">
                        <div className="flex flex-col">
                          <span className="font-mono text-[11px] font-bold text-indigo-600">
                            {format(new Date(log.timestamp), 'HH:mm:ss')}
                          </span>
                          <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                            {format(new Date(log.timestamp), 'dd.MM.yyyy')}
                          </span>
                        </div>
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-500 border border-slate-200 uppercase">
                            {log.person_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <span className="text-sm font-black text-slate-900 tracking-tight">{log.person_name}</span>
                        </div>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className={clsx(
                          "px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border",
                          log.person_type === 'student' ? "bg-blue-50 text-blue-600 border-blue-100" : "bg-purple-50 text-purple-600 border-purple-100"
                        )}>
                          {log.person_type === 'student' ? 'Student' : 'Staff'}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className={clsx(
                          "text-[10px] font-black uppercase tracking-widest",
                          log.event_type === 'in' ? "text-emerald-500" : "text-slate-400"
                        )}>
                          {log.event_display}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-center">
                        <span className={clsx(
                          "px-3 py-1 rounded-lg text-[10px] font-black tracking-widest border",
                          status.bg
                        )}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-center">
                        {log.late_minutes > 0 ? (
                          <span className="text-[11px] font-black text-rose-500">+{log.late_minutes}m</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-2 text-slate-400">
                          <MapPin size={12} />
                          <span className="text-[11px] font-bold">{log.device_name || 'Terminal'}</span>
                        </div>
                      </td>
                      <td className="px-8 py-5 text-right">
                        {log.photo ? (
                          <div className="relative inline-block group/img">
                            <img
                              src={log.photo}
                              className="w-10 h-10 rounded-xl object-cover border border-slate-200 shadow-sm cursor-pointer group-hover/img:brightness-75 transition-all"
                              onClick={() => setSelectedPhoto(log.photo)}
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/img:opacity-100 pointer-events-none transition-opacity">
                              <ExternalLink size={12} className="text-white" />
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {data?.count > 20 && (
          <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
            <p className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em]">
              Page {page} of {Math.ceil(data.count / 20)}
            </p>
            <div className="flex gap-2">
              <button
                className="p-2 rounded-xl bg-white border border-slate-200 text-slate-600 disabled:opacity-30 transition-all hover:bg-slate-50 shadow-sm"
                disabled={!data.previous}
                onClick={() => { setPage((p) => p - 1); window.scrollTo(0, 0) }}
              >
                <ChevronLeft size={18} />
              </button>
              <button
                className="p-2 rounded-xl bg-white border border-slate-200 text-slate-600 disabled:opacity-30 transition-all hover:bg-slate-50 shadow-sm"
                disabled={!data.next}
                onClick={() => { setPage((p) => p + 1); window.scrollTo(0, 0) }}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </section>

      {/* PHOTO MODAL */}
      <Modal
        open={!!selectedPhoto}
        onClose={() => setSelectedPhoto(null)}
        title="Фотография при проходе"
        size="md"
      >
        <div className="flex items-center justify-center py-4">
          {selectedPhoto && (
            <img
              src={selectedPhoto}
              alt="Снимок с терминала"
              className="max-w-full max-h-[70vh] rounded-2xl object-contain shadow-lg border border-slate-100"
            />
          )}
        </div>
      </Modal>
    </div>
  )
}